import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { EyeIcon, EyeSlashIcon, LockClosedIcon } from '@heroicons/react/24/outline'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'

const LoginForm = ({ onLoginSuccess }) => {
  const [showPassword, setShowPassword] = useState(false)
  const [version, setVersion] = useState('')
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setFocus
  } = useForm()

  const loginMutation = useMutation({
    mutationFn: async (password) => {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ password }),
      })
      
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Login failed')
      }
      
      return response.json()
    },
    onSuccess: () => {
      toast.success('登录成功！')
      onLoginSuccess()
    },
    onError: (error) => {
      toast.error(error.message || '登录失败')
    },
  })

  const onSubmit = (data) => {
    loginMutation.mutate(data.password)
  }

  // 组件挂载后自动聚焦到密码输入框并获取版本号
  useEffect(() => {
    setFocus('password')
    
    // 获取版本号
    fetch('/api/version')
      .then(response => response.json())
      .then(data => {
        if (data.version) {
          setVersion(data.version)
        }
      })
      .catch(error => {
        console.error('获取版本号失败:', error)
      })
  }, [setFocus])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-600">
            <LockClosedIcon className="h-6 w-6 text-white" />
          </div>
          <div className="mt-6 text-center">
            <h2 className="inline text-3xl font-extrabold text-white">
              访问受保护的配置管理器
            </h2>
            {version && (
              <span className="ml-3 text-sm text-gray-400 font-normal">
                v{version}
              </span>
            )}
          </div>
          <p className="mt-2 text-center text-sm text-gray-400">
            请输入访问密码以继续
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label htmlFor="password" className="sr-only">
              密码
            </label>
            <div className="relative">
              <input
                {...register('password', {
                  required: '密码不能为空',
                  minLength: { value: 1, message: '密码不能为空' }
                })}
                type={showPassword ? 'text' : 'password'}
                className="appearance-none rounded-lg relative block w-full px-3 py-3 pl-10 pr-10 border border-gray-600 placeholder-gray-400 text-white bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="请输入访问密码"
                disabled={loginMutation.isPending}
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <LockClosedIcon className="h-5 w-5 text-gray-400" />
              </div>
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  type="button"
                  className="text-gray-400 hover:text-white focus:outline-none"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loginMutation.isPending}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            {errors.password && (
              <p className="mt-2 text-sm text-red-400">{errors.password.message}</p>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={loginMutation.isPending}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loginMutation.isPending ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  登录中...
                </div>
              ) : (
                '登录'
              )}
            </button>
          </div>
        </form>

        <div className="text-center">
          <p className="text-xs text-gray-500">
            Homepage 配置管理器 - 安全访问
          </p>
        </div>
      </div>
    </div>
  )
}

export default LoginForm
